
<template>
  <q-page class="ai-chat-page">
    <Teleport v-if="isMounted" to="#custom-header-content">
      <q-toolbar>
        <q-toolbar-title class="text-h6 text-weight-medium" shrink>
          AI Assistant
        </q-toolbar-title>
        <q-space />
        <q-btn-toggle
          v-model="currentMode"
          :options="[
            { label: `${$t('Chat')}`, value: 'chat', icon: 'chat' },
            { label: `${$t('Voice')}`, value: 'voice', icon: 'mic' }
          ]"
          toggle-color="primary"
          :color="$q.dark.isActive ? 'dark' : 'grey-3'"
          class="q-mr-md where-are-you"
          :dense="isNarrowScreen"
        />
        <q-btn flat round icon="settings" @click="showSettings = !showSettings">
          <q-tooltip>{{$t('Settings')}}</q-tooltip>
        </q-btn>
      </q-toolbar>
      <q-slide-transition>
        <div v-show="showSettings" class="q-pa-md bg-grey-1">
          <div class="column q-gutter-md">
            <div class="row justify-start">
              <q-btn flat color="negative" icon="delete_sweep" label="Clear Messages" @click="deleteAllMessages" />
            </div>
            <div v-if="currentMode === 'chat'" class="column q-gutter-sm">
              <div class="row q-gutter-md items-center"><q-space></q-space></div>
            </div>
            <div v-if="currentMode === 'voice'" class="column q-gutter-sm">
              <div class="row q-gutter-md items-center"></div>
            </div>
          </div>
        </div>
      </q-slide-transition>
    </Teleport>

    <!-- Chat Mode -->
    <div v-if="currentMode === 'chat'" class="chat-mode">
      <q-scroll-area ref="messagesScroll" class="messages-container">
        <div class="messages-content q-pa-md">
          <div v-for="message in messages" :key="message.id" class="message-item q-mb-md">
            <div class="row justify-end q-mb-sm">
              <q-card class="user-message bg-primary text-white" style="max-width: 80%">
                <q-card-section class="q-pa-sm">
                  <div class="text-body2">{{ message.prompt }}</div>
                  <div class="row justify-end q-mt-xs q-gutter-xs">
                    <q-btn flat dense round size="sm" :icon="currentlyPlaying === `user-${message.id}` ? 'pause' : 'volume_up'" @click="toggleTTS(message.prompt, `user-${message.id}`)" class="text-white" />
                    <q-btn flat dense round size="sm" icon="edit" @click="editPrompt(message.prompt)" class="text-white" />
                    <q-btn v-if="message.id !== 0" flat dense round size="sm" icon="delete" @click="deleteMessage(message.id)" class="text-white" />
                  </div>
                </q-card-section>
              </q-card>
            </div>
            <div v-if="message.response" class="row justify-start">
              <q-card class="ai-message bg-grey-1" style="max-width: 80%">
                <q-card-section class="q-pa-sm">
                  <div class="text-body2" v-html="cleanResponse(message.response)"></div>
                  <div class="row justify-between items-center q-mt-xs">
                    <span class="text-caption text-grey-6">{{ formatTime(message.timestamp) }}</span>
                    <q-btn flat dense round size="sm" :icon="currentlyPlaying === `ai-${message.id}` ? 'pause' : 'volume_up'" @click="toggleTTS(cleanHTML(message.response), `ai-${message.id}`)" />
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
          <div v-if="isTyping" class="row justify-start">
            <q-card class="ai-message bg-grey-1">
              <q-card-section class="q-pa-sm">
                <div class="typing-dots"><span></span><span></span><span></span></div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-scroll-area>
      <div class="input-area bg-white">
        <div class="row q-pa-md q-gutter-sm">
          <q-input ref="textInput" v-model="prompt" :placeholder="$t('Type your message') + '...'" outlined autogrow :rows="1" :max-height="100" class="col" @keydown="handleKeydown" @input="_0xATH" :disable="isLoading" />
          <q-btn rounded size="md" color="primary" :icon="isLoading ? 'hourglass_empty' : 'send'" @click="sendMessage" :disable="!prompt.trim() || isLoading" :loading="isLoading" />
        </div>
      </div>
    </div>

    <!-- Voice Mode -->
    <div v-else class="voice-mode">
      <div class="voice-container">
        <div class="voice-animation-container">
          <div class="voice-orb" :class="{ 'listening': isListening, 'speaking': isSpeaking, 'processing': isProcessing, 'idle': !isListening && !isSpeaking && !isProcessing }">
            <div class="orb-core"></div>
            <div class="pulse-ring pulse-1"></div>
            <div class="pulse-ring pulse-2"></div>
            <div class="pulse-ring pulse-3"></div>
            <div class="audio-waves">
              <div class="wave wave-1"></div>
              <div class="wave wave-2"></div>
              <div class="wave wave-3"></div>
              <div class="wave wave-4"></div>
              <div class="wave wave-5"></div>
            </div>
          </div>
          <div class="status-text q-mt-lg">
            <h5 class="text-center q-ma-none">{{ currentStatus }}</h5>
            <p class="text-center text-grey-6 q-mt-sm">{{ statusDescription }}</p>
          </div>
        </div>
        <div class="voice-controls">
          <div class="row justify-center q-gutter-md">
            <q-btn fab :color="isListening ? 'negative' : 'primary'" :icon="isListening ? 'mic_off' : 'mic'" @click="toggleListening" :disable="isSpeaking || isProcessing" size="lg">
              <q-tooltip>{{ isListening ? $t('Stop Listening') : $t('Start Listening') }}</q-tooltip>
            </q-btn>
            <q-btn fab color="grey-6" icon="stop" @click="stopAll" :disable="!isListening && !isSpeaking && !isProcessing" size="md">
              <q-tooltip>{{$t('Stop All')}}</q-tooltip>
            </q-btn>
          </div>
        </div>
        <q-card v-if="currentTranscript" class="transcript-card q-mt-md">
          <q-card-section>
            <div class="text-body2"><q-icon name="mic" class="q-mr-sm" />{{ currentTranscript }}</div>
          </q-card-section>
        </q-card>
        <q-card v-if="lastResponse" class="response-card q-mt-md">
          <q-card-section>
            <div class="text-body2">
              <q-icon name="smart_toy" class="q-mr-sm" />
              <span>{{ getPartialText(lastResponse, 'voice-response') }}</span>
              <span v-if="typingProgress['voice-response']?.isActive" class="typing-cursor">|</span>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <q-dialog v-model="showError">
      <q-card>
        <q-card-section><div class="text-h6">Error</div></q-card-section>
        <q-card-section>{{ errorMessage }}</q-card-section>
        <q-card-actions align="right"><q-btn flat label="OK" v-close-popup /></q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import { useNavigation } from '@/common/composables/useNavigation.js'

 
const { useCustomHeader } = useNavigation()

const _0xC1 = { _mdl: 18, _mdn: 'learning', _sr: 1.0, _ct: 800, _wpm: 150 }
const _0xC2 = {
  _gm: 'ChatAI/GetMessages', _sm: 'ChatAI/SendMessage',
  _da: 'ChatAI/DeleteAllMessages', _dm: 'ChatAI/DeleteMessage',
  _ls: 'ai_chat_messages'
}
const _0xC3 = [
  { label:'English (US)',value:'en-US' },{ label:'English (UK)',value:'en-GB' },
  { label:'Croatian',value:'hr-HR' },{ label:'German',value:'de-DE' },
  { label:'French',value:'fr-FR' },{ label:'Spanish',value:'es-ES' },
  { label:'Italian',value:'it-IT' },{ label:'Portuguese',value:'pt-PT' },
  { label:'Russian',value:'ru-RU' },{ label:'Japanese',value:'ja-JP' },
  { label:'Chinese (Mandarin)',value:'zh-CN' }
]
const _0xD1 = (h) => { if(!h)return '';const d=document.createElement('div');d.innerHTML=h;return d.textContent||d.innerText||'' }
const _0xD2 = (r) => r ? r.replaceAll('\\','') : ''
const _0xD3 = (t) => new Date(t).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})
const _0xD4 = (c,n) => { const b=60/c;if(['.','!','?'].includes(n))return b*4;if([',',';',':'].includes(n))return b*2;if(n===' ')return b*0.3;return b }

export default {
  name: 'AIChatVoice',
  emits: ['message-sent','message-deleted','messages-cleared'],

  //  setup() {
  //   const { useCustomHeader } = useNavigation()
    
  //   return {
  //     useCustomHeader
  //   }
  // },
  data() {
    return {
      currentMode:'chat',showSettings:false,isGuest:false,
      messages:[],prompt:'',isLoading:false,isTyping:false,
      selectedModel:{value:_0xC1._mdl,label:_0xC1._mdn},
      isListening:false,isSpeaking:false,isProcessing:false,
      currentTranscript:'',lastResponse:'',
      recognition:null,synthesis:window.speechSynthesis,currentlyPlaying:null,
      availableVoices:[],selectedVoice:null,speechRate:_0xC1._sr,
      showError:false,errorMessage:'',i:null,
      speechLanguage:'en-US',availableLanguages:_0xC3,
      currentlyTyping:null,typingProgress:{},typingSpeed:50,activeTypingTimeout:null,
      isMounted:false
    }
  },

  computed: {
    currentStatus() {
      let s='';if(this.isListening)s=this.$t('Listening');if(this.isProcessing)s=this.$t('Thinking');if(this.isSpeaking)s=this.$t('Speaking');return s?s+'...':this.$t('Ready')
    },
    statusDescription() {
      if(this.isListening)return this.$t('Speak now, I\'m listening');if(this.isProcessing)return this.$t('Thinking about your question');if(this.isSpeaking)return this.$t('Answering your question');return this.$t('Tap the microphone to start')
    },
    getPartialText() {
      return(_0xT,_0xI)=>{const p=this.typingProgress[_0xI];if(!p||!p.isActive)return _0xT;return _0xT.substring(0,p.currentIndex)}
    },
    isNarrowScreen() { return this.$q.screen.width<=450 }
  },

  async mounted() {
    useCustomHeader.value=true;await this._0xI1();this._0xIVR();this._0xLV();this.isMounted=true
  },

  beforeUnmount() { useCustomHeader.value=false;this._0xCU() },

  methods: {
    _0xCT() { if(this.activeTypingTimeout){clearTimeout(this.activeTypingTimeout);this.activeTypingTimeout=null} },
    _0xUTP(_0xI,_0xCI,_0xTL) { this.typingProgress[_0xI]={currentIndex:_0xCI,totalLength:_0xTL,isActive:_0xCI<_0xTL} },

    testTTSVoice() { this._0xST('Hello, this is a test of the selected voice.','test') },
    updateTTSVoice(_0xV) {
      if(this.currentlyPlaying){this.synthesis.cancel();this.currentlyPlaying=null}
      this.selectedVoice=_0xV
      this.$q.notify({message:`${this.$t('TTS Voice changed to')}: ${this.selectedVoice?.name}`,color:'positive',position:'top',timeout:2000,icon:'record_voice_over'})
    },
    updateSpeechLanguage() { if(this.recognition)this.recognition.lang=this.speechLanguage.value },

    async _0xI1() {
      try{
        this.isGuest=!this._0xIA()
        if(this.isGuest){this.messages=this._0xLLS()}else{const r=await this.get(_0xC2._gm);this.messages=r||[]}
        this.i=this.messages.length-1;this._0xSTB()
      }catch(e){this.messages=[];this._0xSE(this.$t('Failed to initialize chat'))}
    },

    _0xIA() { return false },
    _0xLLS() { try{const s=localStorage.getItem(_0xC2._ls);return s?JSON.parse(s):[]}catch(e){return[]} },
    _0xSLS() { try{localStorage.setItem(_0xC2._ls,JSON.stringify(this.messages))}catch(e){} },
    _0xCLS() { try{localStorage.removeItem(_0xC2._ls)}catch(e){} },

    _0xATH() {
      this.$nextTick(()=>{const i=this.$refs.textInput;if(i&&i.$el){const t=i.$el.querySelector('textarea');if(t){t.style.height='auto';t.style.height=Math.min(Math.max(t.scrollHeight,48),120)+'px'}}})
    },

    async sendMessage() {
      if(this.prompt.trim()===''||this.isLoading)return
      const _0xUP=this.prompt.trim(),_0xTI=Date.now()
      this.messages.push({id:_0xTI,prompt:_0xUP,response:null,timestamp:new Date(),isTemp:true})
      this.prompt='';this._0xSTB();this.isLoading=true;this.isTyping=true
      try{
        const _0xR=await this.post(_0xC2._sm,{prompt:_0xUP,saveMessages:(!this.isGuest).toString(),model:this.selectedModel?.label||_0xC1._mdn,model_id:this.selectedModel?.value?.toString()||_0xC1._mdl.toString()})
        if(_0xR){
          const _0xTX=this.messages.findIndex(m=>m.id===_0xTI);if(_0xTX!==-1)this.messages.splice(_0xTX,1)
          const _0xCM={id:_0xR.id||_0xTI,prompt:_0xR.prompt||_0xUP,response:_0xR.response,timestamp:_0xR.timestamp||new Date(),isTemp:false}
          this.messages.push(_0xCM)
          if(this.isGuest)this._0xSLS()
          this.$emit('message-sent',{prompt:_0xCM.prompt,response:_0xCM.response})
          this._0xSTB();this.i=this.messages.length-1
        }
      }catch(e){
        const _0xTX=this.messages.findIndex(m=>m.id===_0xTI);if(_0xTX!==-1)this.messages.splice(_0xTX,1)
        this.prompt=_0xUP;this._0xSE('Failed to send message. Please try again.')
      }finally{this.isLoading=false;this.isTyping=false}
    },

    async deleteAllMessages() {
      try{
        const _0xCF=await this.$q.dialog({title:'Confirm',message:'Are you sure you want to delete all messages?',cancel:true,persistent:true})
        if(_0xCF){
          if(this.isGuest){this._0xCLS()}else{await this.post(_0xC2._da)}
          this.messages=[];this.i=null;this.$emit('messages-cleared')
        }
      }catch(e){if(e!==false)this._0xSE(this.$t('Failed to delete messages'))}
    },

    async deleteMessage(_0xID) {
      try{
        if(!this.isGuest)await this.post(_0xC2._dm,{id:_0xID.toString()})
        const _0xIX=this.messages.findIndex(m=>m.id==_0xID)
        if(_0xIX!==-1){this.messages.splice(_0xIX,1);this.i=this.messages.length-1;if(this.isGuest)this._0xSLS();this.$emit('message-deleted',_0xID)}
      }catch(e){this._0xSE(this.$t('Failed to delete message'))}
    },

    editPrompt(_0xPT) { this.currentMode='chat';this.prompt=_0xPT;this.$nextTick(()=>{if(this.$refs.textInput)this.$refs.textInput.focus()}) },
    previous() { if(this.i!==null&&this.i>0){this.prompt=this.messages[this.i].prompt;this.i--} },
    next() { if(this.i!==null&&this.i<this.messages.length-1){this.i++;this.prompt=this.messages[this.i].prompt} },

    _0xIVR() {
      if(this.recognition)return
      if(!('webkitSpeechRecognition' in window)&&!('SpeechRecognition' in window)){this._0xSE(this.$t('Speech Recognition API not supported in this browser. Try Chrome/Edge.'));return}
      navigator.mediaDevices.getUserMedia({audio:true}).then(()=>{this._0xSSR()}).catch((e)=>{
        const _0xEM={NotAllowedError:this.$t('Microphone access denied. Please click the 🎤 icon in the address bar and allow microphone access.'),NotFoundError:this.$t('No microphone found. Please connect a microphone and refresh.')}
        this._0xSE(_0xEM[e.name]||`${this.$t('Microphone error')}: ${e.message}`)
      })
    },

    _0xSSR() {
      try{
        const _0xSR=window.SpeechRecognition||window.webkitSpeechRecognition
        this.recognition=new _0xSR();this.recognition.continuous=true;this.recognition.interimResults=true;this.recognition.lang='en-US'
        let _0xPR=[],_0xCTO=null
        this.recognition.onstart=()=>{this.isListening=true;this.currentTranscript=this.lastResponse='';_0xPR=[];_0xCTO=null}
        this.recognition.onresult=(e)=>{
          for(let i=e.resultIndex;i<e.results.length;i++){
            if(e.results[i].isFinal){_0xPR.push(e.results[i][0].transcript);if(_0xCTO)clearTimeout(_0xCTO);_0xCTO=setTimeout(()=>{const c=_0xPR.join(' ').trim();if(c.length>3){this.recognition?.stop();this._0xHVI(c)}_0xPR=[]},_0xC1._ct)}
            this.currentTranscript=e.results[i][0].transcript
          }
        }
        this.recognition.onerror=(e)=>{
          this.isListening=false
          const _0xEM={'not-allowed':this.$t('Speech recognition not allowed. Please enable microphone and speech recognition in browser settings.'),'no-speech':this.$t('No speech detected. Please try speaking closer to the microphone.'),'audio-capture':this.$t('Audio capture failed. Please check your microphone.'),'network':this.$t('Network error. Please check your internet connection.'),'service-not-allowed':this.$t('Speech recognition service not allowed. Please check browser permissions.'),'aborted':this.$t('Speech recognition aborted.')}
          this._0xSE(_0xEM[e.error]||`${this.$t('Speech recognition error')}: ${e.error}`)
        }
        this.recognition.onend=()=>{this.isListening=false}
      }catch(e){this._0xSE(this.$t('Failed to initialize speech recognition. Please check browser permissions.'))}
    },

    async _0xHVI(_0xTR) {
      this.currentTranscript=_0xTR;this.isListening=false;this.isProcessing=true
      try{
        const _0xR=await this.post(_0xC2._sm,{prompt:_0xTR,saveMessages:(!this.isGuest).toString(),model:this.selectedModel?.label||_0xC1._mdn,model_id:this.selectedModel?.value?.toString()||_0xC1._mdl.toString()})
        if(_0xR){
          const _0xVM={id:_0xR.id||Date.now(),prompt:_0xR.prompt||_0xTR,response:_0xR.response,timestamp:_0xR.timestamp||new Date()}
          if(this.currentMode==='chat'||this.isGuest){this.messages.push(_0xVM);if(this.isGuest)this._0xSLS()}
          this._0xUTP('voice-response',0,_0xR.response.length)
          this.lastResponse=_0xD1(_0xR.response)
          this._0xSPR(_0xD1(_0xR.response))
        }
      }catch(e){this._0xSE(this.$t('Failed to process voice input'))}
      finally{this.isProcessing=false}
    },

    toggleListening() {
      if(this.isListening){this.recognition?.stop()}
      else{if(!this.recognition){this._0xIVR();return};this.recognition?.start()}
    },

    stopAll() {
      this.recognition?.stop();this.synthesis.cancel()
      this.isListening=false;this.isSpeaking=false;this.isProcessing=false;this.currentlyPlaying=null;this._0xCT()
    },

    _0xLV() {
      const _0xV=this.synthesis.getVoices();this.availableVoices=_0xV
      if(!this.selectedVoice&&_0xV.length>0){
        this.selectedVoice=_0xV.find(v=>v.name.toLowerCase().includes('google')&&(v.lang.startsWith('en-US')||v.lang.startsWith('en_US')))||_0xV.find(v=>v.lang.startsWith('en-US')||v.lang.startsWith('en_US'))||_0xV[0]
      }
    },

    _0xSPR(_0xTX) {
      if(!_0xTX)return;this._0xCT();this.synthesis.cancel();this.isSpeaking=true
      let _0xCI=0
      const _0xU=new SpeechSynthesisUtterance(_0xTX)
      _0xU.voice=this.selectedVoice;_0xU.lang=this.selectedVoice?.lang||'en-US';_0xU.rate=this.speechRate
      _0xU.onstart=()=>{
        this._0xUTP('voice-response',0,_0xTX.length)
        const _0xTNC=()=>{if(_0xCI<_0xTX.length){_0xCI++;this._0xUTP('voice-response',_0xCI,_0xTX.length);this.activeTypingTimeout=setTimeout(_0xTNC,_0xD4(this.speechRate,_0xTX[_0xCI-1]))}}
        _0xTNC()
      }
      _0xU.onend=()=>{this._0xCT();this.isSpeaking=false;this._0xUTP('voice-response',_0xTX.length,_0xTX.length)}
      _0xU.onerror=(e)=>{this._0xCT();this.isSpeaking=false;if(e.error==='canceled'||e.error==='interrupted')return;this._0xSE(this.$t('Speech synthesis error'))}
      this.synthesis.speak(_0xU)
    },

    toggleTTS(_0xTX,_0xMI) { if(this.currentlyPlaying===_0xMI){this.synthesis.cancel();this.currentlyPlaying=null}else{this._0xST(_0xTX,_0xMI)} },

    _0xST(_0xTX,_0xMI) {
      if(!_0xTX)return;this.synthesis.cancel()
      const _0xU=new SpeechSynthesisUtterance(_0xTX);_0xU.voice=this.selectedVoice;_0xU.rate=this.speechRate;_0xU.lang=this.selectedVoice?.lang||'en-US'
      _0xU.onstart=()=>{this.currentlyPlaying=_0xMI};_0xU.onend=()=>{this.currentlyPlaying=null};_0xU.onerror=()=>{this.currentlyPlaying=null}
      this.synthesis.speak(_0xU)
    },

    handleKeydown(_0xE) {
      if(_0xE.key==='Enter'&&!_0xE.shiftKey){_0xE.preventDefault();this.sendMessage()}
      else if(_0xE.key==='ArrowUp'&&_0xE.ctrlKey){_0xE.preventDefault();this.previous()}
      else if(_0xE.key==='ArrowDown'&&_0xE.ctrlKey){_0xE.preventDefault();this.next()}
    },
    _0xSTB() { this.$nextTick(()=>{if(this.$refs.messagesScroll)this.$refs.messagesScroll.setScrollPosition('vertical',99999)}) },
    formatTime(_0xT) { return _0xD3(_0xT) },
    cleanHTML(_0xH) { return _0xD1(_0xH) },
    cleanResponse(_0xR) { return _0xD2(_0xR) },
    _0xSE(_0xM) { this.errorMessage=_0xM;this.showError=true },
    _0xCU() { this.synthesis.cancel();this.recognition?.stop() }
  },

  created() { if(this.synthesis.onvoiceschanged!==undefined)this.synthesis.onvoiceschanged=this._0xLV }
}
</script>

<style lang="scss" scoped>

.q-dialog {
  .q-card {
    body.body--dark & {
      background: $dark;
      color: white;
      box-shadow: none !important; 
    }
  }
}

.ai-message, .user-message {
  .text-body2 {
    font-size: 0.875em !important; 
  }
}

  .text-grey-8 {
  body.body--dark & {
    color: white !important;
  }
}

  .bg-grey-1 {
  body.body--dark & {
    background: $dark-page !important;
  }
}


.ai-chat-page {
  height: calc(100vh - 164px); 
  display: flex;
  flex-direction: column;
}

.chat-mode {
  flex: 1; /* Uzmi sve što ostane */
  display: flex;
  flex-direction: column;

   body.body--dark & {
    background: $dark-page !important;
  }
}

.messages-container {
  flex: 1; /* Auto visina - sve osim input-a */
  // overflow-y: auto !important;
}

.input-area {
  flex-shrink: 0; /* Nikad se ne smanji */
  /* Visina se auto računa! */

   body.body--dark & {
    background: $dark !important;;
    border-top-color: rgba(255, 255, 255, 0.1) !important;
  }
}

.messages-container {
  flex: 1;
  overflow-y: hidden;
}

.messages-content {
  padding-bottom: 20px;

  &.no-messages {

  }

  body.body--dark & {
    background: $dark-page !important;;
  }
}

.no-messages-placeholder {
  text-align: center;
  opacity: 0.6;
}

.user-message {
  border-radius: 18px 18px 4px 18px;

  body.body--dark & {
    box-shadow: none !important; 
  }
}

.ai-message {
  border-radius: 18px 18px 18px 4px;

   body.body--dark & {
    background: $dark !important;
    color: white !important;
    box-shadow: none !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
  }
}

.input-area {
  border-top: 1px solid #e0e0e0;

   body.body--dark & {
    background: $dark !important;;
    border-top-color: rgba(255, 255, 255, 0.1) !important;
  }
}

.input-field {
  .q-field__control {
    min-height: 48px !important;
  }

  .q-field__native {
    min-height: 20px !important;
    line-height: 1.4 !important;
    padding: 12px 16px !important;
  }
}

.send-button {
  flex-shrink: 0;
  width: 48px !important;
  height: 48px !important;
  border-radius: 50% !important;

  .q-btn__wrapper {
    border-radius: 50% !important;
  }
  
}

// Voice Mode
.voice-mode {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.voice-container {
  text-align: center;
  color: white;
  width: 100%;
  max-width: 600px;
}

.voice-animation-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
}

.voice-orb {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.orb-core {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: radial-gradient(circle, #ffffff 0%, #f0f0f0 100%);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  z-index: 3;
}

.pulse-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: pulse 2s infinite;
}

.pulse-1 {
  width: 120px;
  height: 120px;
  animation-delay: 0s;
}

.pulse-2 {
  width: 160px;
  height: 160px;
  animation-delay: 0.7s;
}

.pulse-3 {
  width: 200px;
  height: 200px;
  animation-delay: 1.4s;
}

.audio-waves {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  z-index: 100;
}

.wave {
  position: relative;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
  animation: wave 1.5s infinite ease-in-out;
  transform-origin: center center; // Skaliraj od centra
}

.wave-1 {  width: 4px; height: 20px; animation-delay: 0s; }
.wave-2 {  width: 4px; height: 40px; animation-delay: 0.1s; }
.wave-3 { width: 4px; height: 60px; animation-delay: 0.2s; }
.wave-4 { width: 4px; height: 40px; animation-delay: 0.3s; }
.wave-5 { width: 4px; height: 20px; animation-delay: 0.4s; }

// Voice States
.voice-orb.idle {
  .orb-core {
    background: radial-gradient(circle, #f0f0f0 0%, #d0d0d0 100%);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }
}

.voice-orb.listening {
  .orb-core {
    background: radial-gradient(circle, #4fc3f7 0%, #29b6f6 100%);
    box-shadow: 0 0 40px rgba(79, 195, 247, 0.6);
    animation: listening-pulse 1s infinite alternate;
  }

  .audio-waves {
    opacity: 1;
  }

  .pulse-ring {
    border-color: rgba(79, 195, 247, 0.5);
  }
}

.voice-orb.processing {
  .orb-core {
    background: radial-gradient(circle, #ffb74d 0%, #ff9800 100%);
    box-shadow: 0 0 40px rgba(255, 183, 77, 0.6);
    animation: processing-spin 2s linear infinite;
  }

  .pulse-ring {
    border-color: rgba(255, 183, 77, 0.5);
    animation: processing-pulse 1s infinite ease-in-out;
  }
}

.voice-orb.speaking {
  .orb-core {
    background: radial-gradient(circle, #81c784 0%, #4caf50 100%);
    box-shadow: 0 0 40px rgba(129, 199, 132, 0.6);
    animation: speaking-beat 0.5s infinite alternate;
  }

  .audio-waves {
    opacity: 1;
  }

  .wave {
    background: rgba(129, 199, 132, 0.6);
    animation: speaking-wave 0.8s infinite ease-in-out;
  }

  .pulse-ring {
    border-color: rgba(129, 199, 132, 0.5);
  }
}

.voice-controls {
  margin-top: 2rem;
}

.transcript-card, .response-card {
  max-width: 500px;
  margin: 5px auto;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
}

// Animations
@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

@keyframes wave {
  0%, 100% {
    transform: scaleY(0.6); 
  }
  50% {
    transform: scaleY(1.8);
  }
}

@keyframes listening-pulse {
  0% { transform: scale(1); }
  100% { transform: scale(1.1); }
}

@keyframes processing-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes processing-pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 1; }
}

@keyframes speaking-beat {
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
}

@keyframes speaking-wave {
  0%, 100% {
    height: 20px;
    background: rgba(129, 199, 132, 0.4);
  }
  50% {
    height: 80px;
    background: rgba(129, 199, 132, 0.8);
  }
}

// Typing indicator
.typing-dots {
  display: flex;
  gap: 4px;
  padding: 8px 0;

  span {
    width: 6px;
    height: 6px;
    background: #9ca3af;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.typing-cursor {
  animation: blink 1s infinite;
  font-weight: bold;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

// Responsive
@media (max-width: 600px) {
  .voice-orb {
    width: 150px;
    height: 150px;
  }

  .orb-core {
    width: 60px;
    height: 60px;
  }

  .pulse-1 { width: 90px; height: 90px; }
  .pulse-2 { width: 120px; height: 120px; }
  .pulse-3 { width: 150px; height: 150px; }
}

</style>